import {
  Box,
  Button,
  Checkbox,
  ComboboxItem,
  Flex,
  Grid,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { getHotkeyHandler, useClipboard, useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCheck, IconCopy, IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { repositoryMdm, repositoryPos } from "../../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { MessageResponse } from "../../../../model/MessageResponse";
import { getProvinceSelectNew } from "../../../../service/getSelectApi";
import TimeEstimateInput from "./TimeEstimateInput";

interface TransportConfigurationCreateModel {
  code: string | null;
  name: string | null;
  att1: string | null;
  att2: string | null;
  att3: string | null;
  att4: string | null;
  att5: string | null;
  att6: string | null;
  att7: string | null;
  att8: string | null;
  att9: string | null;
  att10: string | null;
  attr11: string | null;
  attr12: string | null;
  attr13: string | null;
  attr14: string | null;
  attr15: string | null;
  attr16: string | null;
  attr17: string | null;
  attr18: string | null;
  attr19: string | null;
  attr20: string | null;
}

const fieldTooltips: Record<string, string> = {
  code: "Mã định danh loại giao vận (VD: HOA_TOC, NHANH). Dùng trong code để phân biệt loại.",
  name: "Tên hiển thị cho khách hàng trên website khi chọn phương thức giao hàng.",
  att8: "Chọn nơi áp dụng: POS (bán tại quầy), WEB (website), hoặc cả hai.",
  attr16: "Loại đối tác vận chuyển: Nội bộ Hacom, GHTK, Ahamove...",
  att1: "Danh sách tỉnh/TP mà loại GV này áp dụng. Dùng để xác định TP lớn (HN, HCM) khi tính thời gian.",
  att3: "Danh sách phường/xã nội thành - chỉ dùng cho HOA_TOC.",
  att4: "Phí ship đồng giá khi đơn hàng DƯỚI ngưỡng free ship. Áp dụng toàn quốc.",
  att2: "Đơn hàng ≥ ngưỡng này sẽ được MIỄN PHÍ ship. VD: 300,000 VNĐ.",
  att5: "Danh sách TP lớn (HN, HCM) - dùng để ước tính thời gian giao hàng nhanh hơn cho khu vực nội thành.",
  att7: "Số tiền phụ thu CƠ BẢN khi hàng vượt ngưỡng cân nặng cồng kềnh.",
  att6: "Phí cộng thêm cho MỖI KG vượt ngưỡng cồng kềnh (tính trên phí cơ bản).",
  att9: "Ngưỡng KM phân biệt ngoại thành gần/xa cho TP lớn. VD: 50km.",
  att10: "Thời gian giao cho ngoại thành xa (vượt ngưỡng KM). Chỉ áp dụng cho record NHANH.",
  attr17: "Thời gian giao hàng cho KHU VỰC NỘI THÀNH TP lớn (HN, HCM). VD: 2-4h làm việc.",
  attr18: "Thời gian giao cho NGOẠI THÀNH TP lớn, khoảng cách < ngưỡng KM. VD: 4-8h làm việc.",
  attr19: "Thời gian giao cho TỈNH KHÁC, chi nhánh CÙNG TỈNH (nội tỉnh). VD: 1-3 ngày.",
  attr20: "Thời gian giao cho TỈNH KHÁC, chi nhánh KHÁC TỈNH (liên miền). VD: 1-5 ngày.",
  attr11: "Thời gian giao hàng mặc định. Cho HOA_TOC: thời gian chuẩn bị hàng (phút).",
  attr12: "Khoảng cách TỐI ĐA từ KH đến CN (km). Cho HOA_TOC: vượt khoảng cách này sẽ không hiện hoả tốc.",
  attr15: "Đơn hàng ≥ giá trị này sẽ KHÔNG hiển thị hoả tốc. Nhập 0 = không giới hạn.",
  attr13: "Bật/tắt loại giao vận này.",
  attr14: "Nếu tích, loại GV này sẽ KHÔNG giao được hàng cồng kềnh.",
};

const renderLabel = (label: string, fieldKey: string, required?: boolean) => {
  const tip = fieldTooltips[fieldKey];
  const asterisk = required ? (
    <span style={{ color: "var(--mantine-color-error)" }}>{" *"}</span>
  ) : null;
  if (!tip)
    return (
      <>
        {label}
        {asterisk}
      </>
    );
  return (
    <Flex
      component="span"
      align="center"
      gap={4}
      style={{ display: "inline-flex", verticalAlign: "middle" }}
    >
      <span>
        {label}
        {asterisk}
      </span>
      <Tooltip label={tip} multiline w={300} withArrow position="top">
        <IconInfoCircle
          size={14}
          color="#868e96"
          style={{ cursor: "pointer" }}
        />
      </Tooltip>
    </Flex>
  );
};

const EditTransportConfiguration = ({
  data,
  onSuccess,
}: {
  data: any;
  onSuccess: any;
}) => {
  const clipboard = useClipboard({ timeout: 1000 });
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const [dataProvinceSelectNew, setDataProvinceSelectNew] = useState<
    ComboboxItem[]
  >([]);
  const [dataCommuneSelectNew, setDataCommuneSelectNew] = useState<
    ComboboxItem[]
  >([]);
  const [att3Value, setAtt3Value] = useState<string | null>(data?.att3 ?? null);

  const form = useForm<TransportConfigurationCreateModel>({
    mode: "uncontrolled",
    validateInputOnChange: true,
    initialValues: {
      ...data,
    },

    validate: {
      code: (value: string | null) => {
        if (!value || value.length === 0) {
          return "Vui lòng nhập mã loại GV !";
        }
      },
      name: (value: string | null) => {
        if (!value || value.length === 0) {
          return "Vui lòng nhập tên loại GV !";
        }
      },
    },
    transformValues: (values) => ({
      ...values,
      att2: values.att2 ? String(values.att2) : null,
      att4: values.att4 ? String(values.att4) : null,
      att5: values.att5 ? String(values.att5) : null,
      att6: values.att6 ? String(values.att6) : null,
      att7: values.att7 ? String(values.att7) : null,
      att8: values.att8 ? String(values.att8) : null,
      att9: values.att9 ? String(values.att9) : null,
      att10: values.att10 ? String(values.att10) : null,
      attr11: values.attr11 ? String(values.attr11) : null,
      attr12: values.attr12 ? String(values.attr12) : null,
      attr13: values.attr13 ? String(values.attr13) : null,
      attr14: values.attr14 ? String(values.attr14) : null,
      attr15: values.attr15 ? String(values.attr15) : null,
      attr16: values.attr16 ? String(values.attr16) : null,
      attr17: values.attr17 ? String(values.attr17) : null,
      attr18: values.attr18 ? String(values.attr18) : null,
      attr19: values.attr19 ? String(values.attr19) : null,
      attr20: values.attr20 ? String(values.attr20) : null,
    }),
  });

  const handlePasteValueCommune = async () => {
    const text = await navigator.clipboard.readText();

    const data = text + "," + (att3Value || "");

    const dataPaste = [
      ...new Set(
        data
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item !== "")
      ),
    ];

    const dataCommune = dataCommuneSelectNew.map((item: any) => item.value);
    const dataFinal = dataPaste.filter((i) => dataCommune.includes(i));

    const newAtt3 = dataFinal.join(",");
    setAtt3Value(newAtt3);
    form.setValues((prev) => ({
      ...prev,
      att3: newAtt3,
    }));

    if (dataPaste.length !== dataFinal.length) {
      NotificationExtension.Info("Đã lọc bỏ các phường/xã không hợp lệ !");
    }
  };

  const fetchDataProvinceNew = async () => {
    const res = await getProvinceSelectNew();

    if (res) {
      setDataProvinceSelectNew(
        res
          ?.filter((item) => item.id && item.name)
          ?.map((item) => ({
            value: String(item.id),
            label: String(item.name),
          }))
      );
    }
  };

  const fetchDataCommuneNew = async (listId: string[]) => {
    const url = listId.map((i) => `ProvinceIds=${i}`).join("&");

    const res = await repositoryMdm.get<MessageResponse<any>>(
      "/api/v1/TblDmCommuneMoi/get-select?active=true&" + url
    );

    if (res && res.success) {
      const data = res.data
        ?.filter((item: any) => item.text && item.value)
        ?.map((item: any) => ({
          value: String(item.value),
          label: String(item.text),
        }));
      setDataCommuneSelectNew(data);

      // Chỉ validate att3 với danh sách phường/xã khi là HOA_TOC
      // Với các loại khác (NHANH, NV_DV...), att3 chứa ID tỉnh, không phải phường/xã
      if (String(form.getValues().code).toUpperCase() === "HOA_TOC") {
        if (att3Value && String(att3Value)?.length > 0) {
          const dataForm = String(att3Value)?.split(",");
          const dataNew = data.map((item: any) => item.value);
          const dataSet = dataForm.filter((i) => dataNew.includes(i));

          const newAtt3 = dataSet.join(",");
          setAtt3Value(newAtt3);
          form.setValues((prev) => ({
            ...prev,
            att3: newAtt3,
          }));
        }
      }
    }
  };

  const handleEditTransportConfiguration = async (
    dataSubmit: TransportConfigurationCreateModel
  ) => {
    open();
    try {
      const res = await repositoryPos.put<MessageResponse<any>>(
        "/api/v1/WebsiteDelivery/update",
        {
          ...dataSubmit,
          attr13: dataSubmit.attr13 ? Number(dataSubmit.attr13) : null,
        }
      );

      if (res && res.success) {
        NotificationExtension.Success("Chỉnh sửa loại GV thành công !");
        setTimeout(() => {
          modals.closeAll();
          onSuccess();
        }, 1000);
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchDataProvinceNew();
  }, []);

  useEffect(() => {
    if (data?.att1 && String(data?.att1).length > 0) {
      fetchDataCommuneNew(String(data?.att1).split(","));
    }
  }, [data]);

  const selectedProvinceValues =
    form.getValues().att1 && String(form.getValues().att1).length > 0
      ? String(form.getValues().att1).split(",")
      : [];
  const isAllProvinceSelected =
    dataProvinceSelectNew.length > 0 &&
    selectedProvinceValues.length === dataProvinceSelectNew.length;

  const selectedCommuneValues =
    att3Value && String(att3Value).length > 0
      ? String(att3Value).split(",")
      : [];
  const isAllCommuneSelected =
    dataCommuneSelectNew.length > 0 &&
    selectedCommuneValues.length === dataCommuneSelectNew.length;

  return (
    <Box
      component="form"
      w={"60vw"}
      maw={900}
      pt={10}
      onSubmit={form.onSubmit((e: TransportConfigurationCreateModel) =>
        handleEditTransportConfiguration(e)
      )}
    >
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <TextInput
            key={form.key("code")}
            label={renderLabel("Mã loại GV", "code", true)}
            placeholder="Nhập mã loại GV"
            variant="filled"
            readOnly
            {...form.getInputProps("code")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <TextInput
            key={form.key("name")}
            label={renderLabel("Tên loại GV", "name", true)}
            placeholder="Nhập tên loại GV"
            {...form.getInputProps("name")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <Select
            key={form.key("att8")}
            label={renderLabel("Áp dụng", "att8")}
            placeholder="Chọn nơi áp dụng"
            data={[
              { label: "Áp dụng trên POS", value: "POS" },
              { label: "Áp dụng trên WEB", value: "WEB" },
              { label: "Áp dụng ở cả POS và WEB", value: "POS/WEB" },
            ]}
            {...form.getInputProps("att8")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <Select
            key={form.key("attr16")}
            label={renderLabel("Loại vận chuyển", "attr16")}
            placeholder="Chọn loại vận chuyển"
            data={[
              "Nội bộ Hacom",
              "Giao hàng tiết kiệm",
              "Siêu tốc tiết kiệm",
              "Siêu tốc",
              "COD",
            ]}
            {...form.getInputProps("attr16")}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={12}>
          <MultiSelect
            label={renderLabel("Tỉnh/TP áp dụng", "att1")}
            placeholder="Nhập tỉnh/TP áp dụng"
            data={[
              { label: "Tất cả", value: "ALL" },
              ...dataProvinceSelectNew,
            ]}
            value={
              isAllProvinceSelected
                ? ["ALL", ...selectedProvinceValues]
                : selectedProvinceValues
            }
            searchable
            clearable
            nothingFoundMessage="Không tìm thấy dữ liệu !"
            {...form.getInputProps("att1")}
            onChange={(e) => {
              const allProvinceValues = dataProvinceSelectNew.map(
                (item) => item.value
              );
              const clickedAll = e.includes("ALL");

              let finalValues: string[];
              if (clickedAll && !isAllProvinceSelected) {
                // Chọn "Tất cả" -> chọn hết các tỉnh/TP
                finalValues = allProvinceValues;
              } else if (
                !clickedAll &&
                isAllProvinceSelected &&
                e.length === allProvinceValues.length
              ) {
                // Bỏ chọn "Tất cả" -> bỏ hết
                finalValues = [];
              } else {
                finalValues = e.filter((v) => v !== "ALL");
              }

              form.setValues((prev) => ({
                ...prev,
                att1: finalValues.length > 0 ? finalValues.join(",") : null,
              }));

              if (finalValues.length > 0) {
                fetchDataCommuneNew(finalValues);
              }
            }}
          />
        </Grid.Col>
      </Grid>
      {/* Att5: Thành phố lớn - hiển thị cho TẤT CẢ loại GV */}
      <Grid>
        <Grid.Col span={12}>
          <MultiSelect
            label={renderLabel("Thành phố lớn", "att5")}
            placeholder="Chọn thành phố lớn (HN, HCM)"
            data={dataProvinceSelectNew}
            searchable
            clearable
            nothingFoundMessage="Không tìm thấy dữ liệu !"
            {...form.getInputProps("att5")}
            value={
              form.getValues().att5 && String(form.getValues().att5).length > 0
                ? String(form.getValues().att5).split(",")
                : []
            }
            onChange={(e) => {
              form.setValues((prev) => ({
                ...prev,
                att5: e && e.length > 0 ? e.join(",") : null,
              }));
            }}
          />
        </Grid.Col>
      </Grid>
      {/* Att3: Phường/xã nội thành */}
      <Grid>
        <Grid.Col span={12}>
            <MultiSelect
              label={renderLabel("Phường/xã nội thành", "att3")}
              placeholder="Nhập phường/xã nội thành"
              data={[
                { label: "Tất cả", value: "ALL" },
                ...dataCommuneSelectNew,
              ]}
              disabled={
                !form.getValues().att1 ||
                String(form.getValues().att1).length === 0
              }
              rightSection={
                att3Value &&
                String(att3Value).length > 0 && (
                  <Tooltip label={clipboard.copied ? "Đã sao chép" : "Sao chép"}>
                    <IconCopy
                      size={16}
                      color={clipboard.copied ? "#15aabf" : "gray"}
                      onClick={() => {
                        clipboard.copy(att3Value);
                      }}
                    />
                  </Tooltip>
                )
              }
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              {...form.getInputProps("att3")}
              value={
                isAllCommuneSelected
                  ? ["ALL", ...selectedCommuneValues]
                  : selectedCommuneValues
              }
              onChange={(e) => {
                const allCommuneValues = dataCommuneSelectNew.map(
                  (item) => item.value
                );
                const clickedAll = e.includes("ALL");

                let finalValues: string[];
                if (clickedAll && !isAllCommuneSelected) {
                  finalValues = allCommuneValues;
                } else if (
                  !clickedAll &&
                  isAllCommuneSelected &&
                  e.length === allCommuneValues.length
                ) {
                  finalValues = [];
                } else {
                  finalValues = e.filter((v) => v !== "ALL");
                }

                const newAtt3 = finalValues.length > 0 ? finalValues.join(",") : null;
                setAtt3Value(newAtt3);
                form.setValues((prev) => ({
                  ...prev,
                  att3: newAtt3,
                }));
              }}
              onKeyDown={getHotkeyHandler([
                ["mod+V", handlePasteValueCommune],
                ["mod+C", () => clipboard.copy(att3Value)],
              ])}
            />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <NumberInput
            key={form.key("att4")}
            label={renderLabel("Phí đồng giá vận chuyển", "att4")}
            placeholder="Nhập số tiền đồng giá"
            hideControls
            allowNegative={false}
            thousandSeparator=","
            suffix=" VNĐ"
            {...form.getInputProps("att4")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <NumberInput
            key={form.key("att2")}
            label={renderLabel("Ngưỡng free ship", "att2")}
            placeholder="Nhập số tiền giới hạn"
            hideControls
            allowNegative={false}
            thousandSeparator=","
            suffix=" VNĐ"
            {...form.getInputProps("att2")}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <NumberInput
            key={form.key("att9")}
            label={renderLabel("Ngưỡng KM ngoại thành", "att9")}
            placeholder="Nhập số km bắt đầu tính phí"
            hideControls
            allowNegative={false}
            thousandSeparator=","
            suffix=" km"
            min={1}
            {...form.getInputProps("att9")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <NumberInput
            key={form.key("att10")}
            label={renderLabel("TG ngoại thành xa (vượt ngưỡng KM)", "att10")}
            placeholder="Nhập số tiền/1km cộng thêm khi vượt mức"
            hideControls
            allowNegative={false}
            thousandSeparator=","
            suffix=" VNĐ"
            {...form.getInputProps("att10")}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <TimeEstimateInput
            label="TG nội thành TP lớn"
            tooltip={fieldTooltips["attr17"]}
            value={form.getValues().attr17}
            onChange={(val) =>
              form.setValues((prev) => ({ ...prev, attr17: val }))
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <TimeEstimateInput
            label="TG ngoại thành TP lớn"
            tooltip={fieldTooltips["attr18"]}
            value={form.getValues().attr18}
            onChange={(val) =>
              form.setValues((prev) => ({ ...prev, attr18: val }))
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <TimeEstimateInput
            label="TG nội tỉnh"
            tooltip={fieldTooltips["attr19"]}
            value={form.getValues().attr19}
            onChange={(val) =>
              form.setValues((prev) => ({ ...prev, attr19: val }))
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <TimeEstimateInput
            label="TG liên miền"
            tooltip={fieldTooltips["attr20"]}
            value={form.getValues().attr20}
            onChange={(val) =>
              form.setValues((prev) => ({ ...prev, attr20: val }))
            }
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <TextInput
            key={form.key("attr11")}
            label={renderLabel("Thời gian giao hàng mặc định", "attr11")}
            placeholder="Thời gian giao hàng mặc định"
            {...form.getInputProps("attr11")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <NumberInput
            key={form.key("attr12")}
            label={renderLabel("Khoảng cách KH→CN (km)", "attr12")}
            placeholder="Nhập khoảng cách tính từ địa chỉ khách hàng đến chi nhánh"
            hideControls
            allowNegative={false}
            thousandSeparator=","
            suffix=" km"
            min={0}
            {...form.getInputProps("attr12")}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <NumberInput
            key={form.key("attr15")}
            label={renderLabel("Giới hạn giá trị đơn (hoả tốc)", "attr15")}
            placeholder="0 = không giới hạn"
            hideControls
            allowNegative={false}
            thousandSeparator=","
            suffix=" VNĐ"
            {...form.getInputProps("attr15")}
          />
        </Grid.Col>
      </Grid>
      <Grid mt={10}>
        <Flex gap={"sm"}>
          <Checkbox
            label="Hoạt động"
            checked={
              Number(form.getValues().attr13) === 1 ? true : false
            }
            onChange={(e) =>
              form.setValues((prev) => ({
                ...prev,
                attr13: e.currentTarget.checked ? "1" : "0",
              }))
            }
          />
          <Checkbox
            label="Không gh cồng kềnh "
            checked={
              Number(form.getValues().attr14) === 1 ? true : false
            }
            onChange={(e) =>
              form.setValues((prev) => ({
                ...prev,
                attr14: e.currentTarget.checked ? "1" : "0",
              }))
            }
          />
        </Flex>
      </Grid>
      <Flex mt={10} justify={"end"} gap={"xs"}>
        <Button
          leftSection={<IconCheck size={16} />}
          color="green"
          type="submit"
          loading={visible}
        >
          Lưu
        </Button>
      </Flex>
    </Box>
  );
};

export default EditTransportConfiguration;
